import { CourseService } from '@/courses/services/courses.service';
import { ICourseWithResource } from '@/core/interfaces/course-with-resource.interface';
import { EduacProvider } from '../providers/eduac.provider';
import { IResultDownloadableResource } from '@/courses/interfaces/result-downloadable-resource.interface';
import { RequestProvider } from '@/shared/providers/request.provider';

class SynchronizationServicesClass {

  public async getContentList() : Promise<ICourseWithResource[]> {
    const classWithCourseList = await CourseService.getClassContents() as any;

    const courseMap = new Map<number, ICourseWithResource>();
    for( const classWithCourse of classWithCourseList.content ) {
      courseMap[ classWithCourse.content.id ] = { 
        contentId: classWithCourse.content.id, 
        contentTitle: classWithCourse.content.title,
        resourceList: null,
        resourceTotal: 0,
        resourceTotalCached: 0,
      };
    }
    
    const courseList = Object.values(courseMap);
    courseList.sort((c1,c2)=> c2.contentId - c1.contentId);
    return courseList;
  }

  public async populateCourseListWithFileInfo(courseList:ICourseWithResource[], logger:Function) : Promise<ICourseWithResource[]> {
    for( const course of courseList ) {
      const contentTitle = course.contentTitle;
      logger(`${EduacProvider.prototype.$vue.$t('tools.synchronization.msgSynchronizationCourseLoading', {contentTitle})}`);

      let downloadableResourceList = {materials:[], sections:[]};
      try{
        downloadableResourceList = await CourseService.getContentDownloadableResources( course.contentId );
      } catch( ex ) {
        //when it loses network connection, ex is undefined in electron environment
        course.errorMessage = `[contentId:${course.contentId}]${ex?.name}: ${ex?.message}`;
      }

      const materialList = downloadableResourceList.materials || [];
      const sectionlList = downloadableResourceList.sections || [];
      const resourceMap:IResultDownloadableResource[] = [];
      
      for(const material of materialList ) {
        resourceMap[ material.hash ] = material;
      }

      for(const section of sectionlList ) {
        
        //do not include this type of objects
        if( section.type == 'LESSON_PPT_HTML' ) {
          continue;
        }

        if( section.resources?.length > 0 ) {
          for( const imageResource of section.resources ) {
            resourceMap[ imageResource.hash ] = {...imageResource, name: section.name, id: section.id };
          }
        } else {
          resourceMap[ section.hash ] = section;
        }
      }

      const resourceList = Object.values(resourceMap);

      var resourceCached = 0;
      for(const resource of resourceList ) {
        const fileResponse = await EduacProvider.prototype.$vue.$FilesCache.getFile( resource.hash );
        if( fileResponse.file ) {
          resourceCached++;
        }
      }

      course.resourceList = resourceList;
      course.resourceTotalCached = resourceCached;
    }
    
    return courseList;
  }


  public async downloadFilesFromCourse(course:ICourseWithResource, logger:Function) {    
      //re-calculate which files are cached and which aren't
      const resourceList = [];
      course.resourceTotalCached = 0;
      for(const resource of course.resourceList ) {
        const fileResponse = await EduacProvider.prototype.$vue.$FilesCache.getFile( resource.hash );
        if( fileResponse.file ) {
          course.resourceTotalCached++;
        } else {
          resourceList.push(resource);
        }
      }

      var fileCounter = 0;
      const errorList = [];
      for(const resource of resourceList ) {
        fileCounter++;
        try{
          const resourceCounter = `[${fileCounter}/${resourceList.length}]`;
          const resourceName = resource.name;
          logger(`${EduacProvider.prototype.$vue.$t('tools.synchronization.msgSynchronizationResource', {resourceCounter, resourceName})}`);

          let response = {result:false, error: null};

          if( !resource.url || !resource.hash ) {
            throw 'resource has no info for downloading';
          }

          if( resource.mimeType === 'application/pdf' ) {
            response = await this.downloadPdfFile(resource.url, resource.hash);
          } else {
            response = await this.downloadNonPdfFile(resource.url, resource.hash);
          }

          if( response.error ) {
            if( response.error.name == 'QuotaExceededError' ) {
              throw response.error;
            }

            errorList.push({
              message: `FileCacheError contentId[${course.contentId}] resource[${resource.id}] ${resource.name}`, 
              source: response.error ,
            });

          } else if ( response.result ) {
            course.resourceTotalCached++;
          }

        }catch(error) {
          
          if( error.name == 'QuotaExceededError' || error.message == 'SynchProcessAbortedError') {
            throw error;
          }

          const resourceAsString = JSON.stringify(resource).replaceAll('"','\'');          
          const errorMsg = {
            message: `SynchronizationError contentId[${course.contentId}] section/material ${resourceAsString}`, 
            source: error ,
          };
          errorList.push(errorMsg);
        }
      }

      return errorList;
  }


  private async downloadPdfFile(fileUrl, fileHash) : Promise<any> {
    const loadingTask = pdfjsLib.getDocument({url: fileUrl, stopAtErrors:true, withCredentials: true});
    return loadingTask.promise.then( async function(pdf) {
      const data = await pdf.getData();
      const response = await EduacProvider.prototype.$vue.$FilesCache.cacheFile(fileHash, data);
      return response;
    })
    .finally(() => {
      loadingTask.destroy(); 
    });
  }

  private async downloadNonPdfFile(fileUrl, fileHash) : Promise<any> {
    let responseDownload = await RequestProvider.requestPublicUrl(fileUrl);
    
    //fix when downloading empty files
    if( responseDownload.status == 200 && responseDownload.data == '' ) {
      responseDownload = new Uint8Array(0);
    }

    const response = await EduacProvider.prototype.$vue.$FilesCache.cacheFile(fileHash, responseDownload);
    return response;
  }

}

export default new SynchronizationServicesClass();
